/**
 * MTM-1 — the live position model, keyed by (tradingsymbol, PRODUCT).
 *
 * THE DEFECT THIS REPLACES. Flattrade returns one PositionBook row PER PRODUCT
 * for the same trading symbol — observed on the operator's own account
 * 2026-08-14:
 *
 *     tsym: NIFTY18AUG26P24600   prd: M   netqty: 0
 *     tsym: NIFTY18AUG26P24600   prd: I   netqty: 0
 *
 * live.html kept `positions[tsym]` and stored `prd` as a FIELD, so the second
 * row overwrote the first and which one survived depended on the broker's array
 * order. The MTM mismatch the operator reported was the mildest symptom. The
 * model feeds `lastPositions`, which feeds:
 *
 *   squareOffAll  — filters netqty !== 0. If the FLAT product won the overwrite,
 *                   the live leg is invisible and the button reports "No open
 *                   positions" while a real position is open.
 *   evalExits     — finds by tsym. A flat survivor means an armed STOPLOSS or
 *                   TARGET never fires; and when both legs are live it fires the
 *                   first, then deletes the rule, leaving the second leg open
 *                   with its exit silently gone.
 *
 * Both were the live real-money path on 2026-08-14 (EXIT_WATCH_SERVERSIDE=1 with
 * LIVE_FIRE off is DRY-RUN, so /api/live-exits returns firing:false and the
 * client owns firing).
 *
 * This module exists so that chain can be TESTED rather than read. It is pure —
 * no DOM, no fetch — and live.html delegates to it, which is also what makes the
 * "must fail against today's code" demonstration possible at all.
 */

/** Marker for a broker row that arrived with no product code. */
export const MISSING_PRODUCT = "?";

export interface BrokerPositionRow {
  tsym?: string;
  prd?: string;
  exch?: string;
  netqty?: string | number;
  netavgprc?: string | number;
  avgprc?: string | number;
  rpnl?: string | number;
  lp?: string | number;
}

export interface ModelPosition {
  key: string;
  tsym: string;
  prd: string;
  exch: string;
  netqty: number;
  avg: number;
  realized: number;
  lp: number;
  fysym: string | null;
  insertedAt: number;
  confirmed: boolean;
}

/**
 * The model key: tradingsymbol AND product.
 *
 * A missing product is NOT defaulted to "M". live.html's `row.prd || 'M'`
 * invented a product the broker never sent, and under a composite key that
 * fabricates a whole model entry. It is marked instead, so a caller can see it
 * and say so rather than silently trading against a guess.
 */
export function positionKey(tsym: string | null | undefined,
                            prd: string | null | undefined): string {
  const t = String(tsym == null ? "" : tsym).trim();
  if (!t) return "";
  const p = String(prd == null ? "" : prd).trim();
  return `${t}|${p || MISSING_PRODUCT}`;
}

/** The (tsym, prd) a model key was built from. */
export function splitPositionKey(key: string): { tsym: string; prd: string } {
  const raw = String(key == null ? "" : key);
  const at = raw.lastIndexOf("|");
  if (at < 0) return { tsym: raw, prd: "" };
  return { tsym: raw.slice(0, at), prd: raw.slice(at + 1) };
}

/** True when the row carried no product code — surfaced, never silently filled. */
export function hasMissingProduct(key: string): boolean {
  return splitPositionKey(key).prd === MISSING_PRODUCT;
}

/**
 * A DOM-id fragment for one leg — DECIDED DELIBERATELY, because live.html's
 * `cid` sanitizer is NOT injective.
 *
 * cid (live.html:743) is `pre + '-' + String(t).replace(/[^A-Za-z0-9]/g, '')`.
 * Feeding it a raw composite key strips the separator, so:
 *
 *     "NIFTY18AUG26P24600|M"  ->  "NIFTY18AUG26P24600M"
 *     "NIFTY18AUG26P24600|?"  ->  "NIFTY18AUG26P24600"   <- today's tsym-only id
 *
 * and a tradingsymbol ENDING in "M" with no product would collide with that same
 * symbol under product M. Unlikely with real Flattrade symbols, but a DOM id
 * collision means two legs writing into one cell — the same class of bug as the
 * model collision this whole change removes, one layer down.
 *
 * So the parts are sanitized SEPARATELY and joined with "-", a character the
 * sanitizer strips from both parts and therefore one neither can contain. That
 * makes the mapping injective. A key with no product separator (the
 * "__LATBURST__" synthetic) passes through as a single sanitized token and
 * cannot collide with a two-part one, because it contains no "-".
 */
export function domToken(key: string | null | undefined): string {
  const san = (v: unknown) => String(v == null ? "" : v).replace(/[^A-Za-z0-9]/g, "");
  const raw = String(key == null ? "" : key);
  if (raw.indexOf("|") < 0) return san(raw);
  const { tsym, prd } = splitPositionKey(raw);
  return `${san(tsym)}-${san(prd) || "X"}`;
}


function num(value: unknown): number {
  const n = parseFloat(String(value == null ? "" : value));
  return isFinite(n) ? n : 0;
}

/**
 * Fold a PositionBook response into the model, keyed per product.
 *
 * Returns the keys seen in this response, so the caller can sweep the ones that
 * vanished — the sweep must compare KEYS, or closing one product deletes the
 * other product's row.
 */
export function reconcileRows(
  rows: BrokerPositionRow[] | null | undefined,
  positions: Record<string, ModelPosition>,
): { seen: Set<string>; missingProduct: string[] } {
  const seen = new Set<string>();
  const missingProduct: string[] = [];
  for (const row of rows || []) {
    const tsym = row && row.tsym ? String(row.tsym) : "";
    if (!tsym) continue;
    const key = positionKey(tsym, row.prd);
    seen.add(key);
    if (hasMissingProduct(key)) missingProduct.push(tsym);

    let p = positions[key];
    if (!p) {
      p = positions[key] = {
        key, tsym, prd: splitPositionKey(key).prd, exch: row.exch || "NFO",
        netqty: 0, avg: 0, realized: 0, lp: 0,
        fysym: null, insertedAt: 0, confirmed: true,
      };
    }
    p.netqty = parseInt(String(row.netqty || 0), 10) || 0;
    p.avg = num(row.netavgprc || row.avgprc || 0) || p.avg;
    p.realized = num(row.rpnl);
    p.lp = num(row.lp) || p.lp;
    if (row.exch) p.exch = row.exch;
    if (row.prd) p.prd = String(row.prd);
    p.confirmed = true;
  }
  return { seen, missingProduct };
}

/** Keys the broker no longer reports, past their optimistic grace window. */
export function staleKeys(
  positions: Record<string, ModelPosition>, seen: Set<string>,
  now: number, graceMs = 8000,
): string[] {
  return Object.keys(positions).filter(key => {
    if (seen.has(key)) return false;
    const p = positions[key];
    return !(p && !p.confirmed && (now - p.insertedAt) < graceMs);
  });
}

export interface LegacyPosition {
  key: string;
  tsym: string;
  exch: string;
  prd: string;
  netqty: number;
  netavgprc: number;
  avgprc: number;
  lp: number;
  rpnl: number;
  urmtom: number;
}

/**
 * The broker-shaped mirror `evalExits` / `squareOffAll` / the exit modal read.
 *
 * It carries `key` as well as `tsym` precisely so those consumers can address
 * ONE product. Dropping the key here is what would re-create the defect one
 * level down.
 */
export function toLastPositions(
  positions: Record<string, ModelPosition>,
  liveLp: (p: ModelPosition) => number,
  unrealizedOf: (p: ModelPosition) => number,
): LegacyPosition[] {
  return Object.values(positions || {}).map(p => ({
    key: p.key, tsym: p.tsym, exch: p.exch, prd: p.prd, netqty: p.netqty,
    netavgprc: p.avg, avgprc: p.avg, lp: liveLp(p),
    rpnl: p.realized, urmtom: unrealizedOf(p),
  }));
}

/** Every leg with a non-zero net — what SQUARE OFF ALL must close. */
export function openLegs(last: LegacyPosition[] | null | undefined): LegacyPosition[] {
  return (last || []).filter(p => parseInt(String(p.netqty || 0), 10) !== 0);
}

/**
 * The leg an armed exit belongs to, addressed BY KEY.
 *
 * Matching on tsym alone is the evalExits half of this defect: with two
 * products it takes whichever comes first, fires it, and then removes a rule the
 * other leg still needs.
 */
export function findLeg(last: LegacyPosition[] | null | undefined,
                        key: string): LegacyPosition | null {
  const open = openLegs(last).filter(p => p.key === key);
  return open.length ? open[0] : null;
}

/** Total realised + unrealised across EVERY leg (the MTM pill). */
export function totalMtm(positions: Record<string, ModelPosition>,
                         unrealizedOf: (p: ModelPosition) => number):
                         { realized: number; unrealized: number; total: number } {
  let realized = 0;
  let unrealized = 0;
  for (const p of Object.values(positions || {})) {
    realized += num(p.realized);
    unrealized += num(unrealizedOf(p));
  }
  return { realized, unrealized, total: realized + unrealized };
}
